/**
 * The New contest dialog (ISSUE-1042). Mounted once by `PoolScreenPane` for
 * the Commissioner and Admins, and opened by `?dialog=new-contest` from the
 * Contests card in Settings, the Results and Game no-contest states, and the
 * hand-off after "Start new season".
 *
 * It reads the season list (no current season means no form, since
 * /contest/create would refuse), the contest list, and the latest contest's
 * config for the owner default A prefill. Rules the Commissioner saved while
 * creating the pool before the schedule was out win over that copy
 * (ISSUE-1316), and /contest/create clears them. It guards unsaved edits, saves
 * through /contest/create, shows each refusal inline (R6), and on success
 * refreshes the contest reads by hand and closes. The generated hook's own
 * invalidation runs 150ms later, which is too late for the Contests card to
 * show the new row the moment the dialog closes.
 */
import {
  useContestCreate,
  useContestGet,
  useContestList,
  useContestPendingSetupGet,
  useSeasonList,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  confirmDestructive,
  Dialog,
  InlineBanner,
  OVERLAY_FOOTER_LAYOUT,
  Skeleton,
} from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import { useOverlay } from "../../routeState/useOverlay";
import {
  CREATE_POOL_MISSING_ELEMENT_ID,
  CreatePoolMissingSummary,
} from "../createPool/CreatePoolMissingSummary";
import { createFailureTreatment } from "./newContestErrors";
import { NewContestForm } from "./NewContestForm";
import {
  buildInitialNewContestDraft,
  buildNewContestCreateBody,
  buildNewContestDraftFromPendingSetup,
  hasCurrentSeason,
  isNewContestDraftDirty,
  missingNewContestItems,
  NEW_CONTEST_CANCEL_LABEL,
  NEW_CONTEST_CLOSE_LABEL,
  NEW_CONTEST_DIALOG_DESCRIPTION,
  NEW_CONTEST_DIALOG_NAME,
  NEW_CONTEST_DIALOG_TITLE,
  NEW_CONTEST_DISCARD_BODY,
  NEW_CONTEST_DISCARD_CANCEL_LABEL,
  NEW_CONTEST_DISCARD_CONFIRM_LABEL,
  NEW_CONTEST_DISCARD_TITLE,
  NEW_CONTEST_FAILED_MESSAGE,
  NEW_CONTEST_LOADING_LABEL,
  NEW_CONTEST_NO_SEASON_BODY,
  NEW_CONTEST_PENDING_SETUP_NOTE,
  NEW_CONTEST_RETRY_LABEL,
  NEW_CONTEST_SUBMIT_LABEL,
  NEW_CONTEST_SUBMITTING_LABEL,
  newContestPrefillNote,
  pickSourceContest,
  resolveNewContestGate,
  type typeNewContestDraft,
  type typeNewContestSource,
} from "./newContestModel";

// The query key prefixes a new contest changes. The pending setup read goes
// empty once the pool's first contest exists (ISSUE-1316).
const CONTEST_QUERY_KEY_PREFIXES = [
  "post:contest:contestList",
  "post:contest:contestGet",
  "post:contest:contestPendingSetupGet",
];

export type typeNewContestDialogContainerProps = { poolId: string };

export function NewContestDialogContainer(
  props: typeNewContestDialogContainerProps,
): ReactNode {
  const { poolId } = props;
  const overlay = useOverlay();
  const queryClient = useQueryClient();
  const open = overlay.isOpen("dialog", NEW_CONTEST_DIALOG_NAME);

  const seasons = useSeasonList({ poolId }, { enabled: open });
  const contests = useContestList({ poolId }, { enabled: open });
  const source = pickSourceContest(contests.data?.data?.items ?? []);
  const sourceContest = useContestGet(
    { poolId, contestId: source?.contestId ?? "" },
    { enabled: open && source !== null },
  );
  const pendingSetup = useContestPendingSetupGet({ poolId }, { enabled: open });
  const savedSetup = pendingSetup.data?.data?.setup ?? null;
  const create = useContestCreate();

  const hasFailed =
    seasons.isError ||
    contests.isError ||
    pendingSetup.isError ||
    extractErrorCode(seasons.data) !== null ||
    extractErrorCode(contests.data) !== null ||
    extractErrorCode(pendingSetup.data) !== null ||
    (source !== null &&
      (sourceContest.isError || extractErrorCode(sourceContest.data) !== null));
  const isLoading =
    seasons.isLoading ||
    contests.isLoading ||
    pendingSetup.isLoading ||
    (source !== null && sourceContest.isLoading);
  const gate = resolveNewContestGate({
    isLoading,
    hasFailed,
    hasCurrentSeason: hasCurrentSeason(seasons.data?.data?.items ?? []),
  });

  const [draft, setDraft] = useState<typeNewContestDraft | null>(null);
  const [baseline, setBaseline] = useState<typeNewContestDraft | null>(null);
  // The contest whose rules actually seeded the draft. Null when the draft
  // fell back to the wizard defaults, so the prefill line never claims a copy
  // that did not happen.
  const [seededFrom, setSeededFrom] = useState<typeNewContestSource | null>(
    null,
  );
  // ISSUE-1316. True when the draft started from the rules saved at pool
  // creation, which take the place of the copy above.
  const [isSeededFromPendingSetup, setIsSeededFromPendingSetup] =
    useState(false);
  const [inlineNote, setInlineNote] = useState<string | null>(null);

  useEffect(() => {
    if (!open || gate !== "ready" || draft !== null) return;
    if (savedSetup !== null) {
      const fromSetup = buildNewContestDraftFromPendingSetup({
        setup: savedSetup,
        now: new Date(),
      });
      setDraft(fromSetup);
      setBaseline(fromSetup);
      setSeededFrom(null);
      setIsSeededFromPendingSetup(true);
      return;
    }
    const sourceConfig =
      source === null ? null : (sourceContest.data?.data?.config ?? null);
    const initial = buildInitialNewContestDraft({
      sourceConfig,
      now: new Date(),
    });
    setDraft(initial);
    setBaseline(initial);
    setSeededFrom(sourceConfig === null ? null : source);
    setIsSeededFromPendingSetup(false);
  }, [open, gate, draft, sourceContest.data, savedSetup]);

  useEffect(() => {
    if (open) return;
    setDraft(null);
    setBaseline(null);
    setSeededFrom(null);
    setIsSeededFromPendingSetup(false);
    creationRequest.current = null;
    setInlineNote(null);
  }, [open]);

  const prefillNote = isSeededFromPendingSetup
    ? NEW_CONTEST_PENDING_SETUP_NOTE
    : seededFrom === null
      ? null
      : newContestPrefillNote(seededFrom.name);
  const isDirty =
    draft !== null &&
    baseline !== null &&
    isNewContestDraftDirty(draft, baseline);

  function close(): void {
    overlay.close({ kind: "dialog" });
  }

  async function handleRequestClose(): Promise<void> {
    if (!isDirty) {
      close();
      return;
    }
    const discard = await confirmDestructive({
      title: NEW_CONTEST_DISCARD_TITLE,
      body: NEW_CONTEST_DISCARD_BODY,
      confirmLabel: NEW_CONTEST_DISCARD_CONFIRM_LABEL,
      cancelLabel: NEW_CONTEST_DISCARD_CANCEL_LABEL,
    });
    if (discard) close();
  }

  const creationRequest = useRef<{ body: string; id: string } | null>(null);
  async function handleSubmit(): Promise<void> {
    if (draft === null) return;
    const body = buildNewContestCreateBody({ poolId, draft });
    if (body === null) return;

    setInlineNote(null);
    try {
      const fingerprint = JSON.stringify(body);
      if (creationRequest.current?.body !== fingerprint)
        creationRequest.current = {
          body: fingerprint,
          id: crypto.randomUUID(),
        };
      const result = await create.mutateAsync({
        ...body,
        requestId: creationRequest.current.id,
      });
      const code = extractErrorCode(result);
      if (code !== null) {
        const rawReason = result.error?.details?.reason;
        const reason = typeof rawReason === "string" ? rawReason : null;
        const treatment = createFailureTreatment({ code, reason });
        if (treatment.kind === "inline") {
          setInlineNote(treatment.note);
          return;
        }
        showApiError(result);
        return;
      }
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const first = query.queryKey[0];
          return (
            typeof first === "string" &&
            CONTEST_QUERY_KEY_PREFIXES.includes(first)
          );
        },
      });
      close();
    } catch (error) {
      if (!showApiError(error)) showOfflineError();
    }
  }

  let body: ReactNode;
  if (gate === "failed") {
    body = (
      <div data-testid="new-contest-failed">
        <InlineBanner
          variant="warning"
          message={NEW_CONTEST_FAILED_MESSAGE}
          action={{
            label: NEW_CONTEST_RETRY_LABEL,
            onAction: () => {
              void seasons.refetch();
              void contests.refetch();
              void pendingSetup.refetch();
              if (source !== null) void sourceContest.refetch();
            },
          }}
        />
      </div>
    );
  } else if (gate === "noSeason") {
    body = (
      <div data-testid="new-contest-no-season">
        <InlineBanner
          variant="neutral"
          message={
            savedSetup !== null
              ? `Your selected rules are saved. ${NEW_CONTEST_NO_SEASON_BODY}`
              : NEW_CONTEST_NO_SEASON_BODY
          }
        />
      </div>
    );
  } else if (gate === "ready" && draft !== null) {
    body = (
      <NewContestForm
        draft={draft}
        prefillNote={prefillNote}
        inlineNote={inlineNote}
        onChangeRules={(rules) => {
          setDraft({ ...draft, rules });
        }}
      />
    );
  } else {
    body = (
      <div data-testid="new-contest-loading">
        <Skeleton shape="card" label={NEW_CONTEST_LOADING_LABEL} />
      </div>
    );
  }

  let footer: ReactNode;
  if (gate === "ready" && draft !== null) {
    const missing = missingNewContestItems(draft);
    footer = (
      <div className="flex w-full flex-col gap-2">
        <CreatePoolMissingSummary items={missing} />
        <div className={OVERLAY_FOOTER_LAYOUT}>
          <Button
            variant="secondary"
            onClick={() => {
              void handleRequestClose();
            }}
            data-testid="new-contest-cancel"
          >
            {NEW_CONTEST_CANCEL_LABEL}
          </Button>
          <Button
            loading={create.isPending}
            loadingLabel={NEW_CONTEST_SUBMITTING_LABEL}
            disabled={missing.length > 0 || create.isPending}
            aria-describedby={
              missing.length > 0 ? CREATE_POOL_MISSING_ELEMENT_ID : undefined
            }
            onClick={() => {
              void handleSubmit();
            }}
            data-testid="new-contest-submit"
          >
            {NEW_CONTEST_SUBMIT_LABEL}
          </Button>
        </div>
      </div>
    );
  } else {
    footer = (
      <Button
        variant="secondary"
        onClick={close}
        data-testid="new-contest-close"
      >
        {NEW_CONTEST_CLOSE_LABEL}
      </Button>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      onRequestClose={() => {
        void handleRequestClose();
      }}
      isDirty={isDirty}
      title={NEW_CONTEST_DIALOG_TITLE}
      description={NEW_CONTEST_DIALOG_DESCRIPTION}
      footer={footer}
    >
      {body}
    </Dialog>
  );
}
